import { useEffect, useMemo, useState } from 'react'
import { Plus, ShieldCheck, Trash2, X } from 'lucide-react'
import { supabase } from '../supabase'
import { useToast } from '../Toast'
import { useUser } from '../UserContext'
import { ALL_PERMISSIONS, PERMISSION_GROUPS } from '../lib/permissions'
import ConfirmModal from '../ConfirmModal'

const EMPTY_FORM = { name: '', description: '', permissions: [] }

export default function PermissionSets() {
  const toast = useToast()
  const { orgId, can } = useUser()
  const [sets, setSets] = useState([])
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(null)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)

  useEffect(() => {
    if (orgId) fetchSets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId])

  async function fetchSets() {
    const { data, error } = await supabase
      .from('permission_sets')
      .select('*')
      .eq('org_id', orgId)
      .order('is_system', { ascending: false })
      .order('name')

    if (error) toast.error(`Failed to load permission sets: ${error.message}`)
    setSets(data || [])
    setLoading(false)
  }

  function openCreate() {
    setEditing('new')
    setForm(EMPTY_FORM)
  }

  function openEdit(permissionSet) {
    setEditing(permissionSet.id)
    setForm({
      name: permissionSet.name,
      description: permissionSet.description || '',
      permissions: permissionSet.permissions || []
    })
  }

  function closeEditor() {
    setEditing(null)
    setForm(EMPTY_FORM)
  }

  function togglePermission(key) {
    setForm(current => ({
      ...current,
      permissions: current.permissions.includes(key)
        ? current.permissions.filter(permission => permission !== key)
        : [...current.permissions, key]
    }))
  }

  async function saveSet() {
    if (!form.name.trim()) {
      toast.error('Permission set name is required')
      return
    }

    setSaving(true)
    const payload = {
      org_id: orgId,
      name: form.name.trim(),
      description: form.description.trim() || null,
      permissions: form.permissions
    }

    const { error } = editing === 'new'
      ? await supabase.from('permission_sets').insert(payload)
      : await supabase.from('permission_sets').update(payload).eq('id', editing).eq('org_id', orgId)

    setSaving(false)
    if (error) {
      toast.error(`Failed to save permission set: ${error.message}`)
      return
    }

    toast.success(editing === 'new' ? 'Permission set created' : 'Permission set updated')
    closeEditor()
    fetchSets()
  }

  async function deleteSet() {
    const { error } = await supabase
      .from('permission_sets')
      .delete()
      .eq('id', deleteTarget.id)
      .eq('org_id', orgId)

    if (error) toast.error(`Failed to delete permission set: ${error.message}`)
    else {
      toast.success('Permission set deleted')
      fetchSets()
    }
    setDeleteTarget(null)
  }

  const selectedSet = useMemo(
    () => sets.find(permissionSet => permissionSet.id === editing),
    [editing, sets]
  )

  if (!can('permissions.manage')) return null

  return (
    <div className="hv-page" style={{ maxWidth: 1180 }}>
      <style>{`
        .permission-modal-overlay {
          position: fixed; inset: 0; z-index: 9999; padding: 24px;
          display: flex; align-items: center; justify-content: center;
          background: rgba(15, 23, 42, 0.42);
        }
        .permission-modal {
          width: 680px; max-width: 100%; max-height: calc(100vh - 48px);
          overflow-y: auto; padding: 24px; background: #fff;
          border: 1px solid var(--hv-border); border-radius: 14px;
          box-shadow: 0 24px 80px rgba(15, 23, 42, 0.22);
        }
      `}</style>
      <div className="hv-page-header">
        <div>
          <h1 className="hv-page-title">Permission Sets</h1>
          <p className="hv-page-subtitle">Control what different groups can view and manage.</p>
        </div>
        <button className="hv-btn hv-btn-primary" onClick={openCreate}>
          <Plus size={16} /> New Permission Set
        </button>
      </div>

      {loading ? (
        <div className="hv-card" style={{ padding: 28, color: 'var(--hv-text-muted)' }}>Loading permission sets...</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(285px, 1fr))', gap: 16 }}>
          {sets.map(permissionSet => (
            <button
              key={permissionSet.id}
              type="button"
              className="hv-card"
              onClick={() => openEdit(permissionSet)}
              style={{ padding: 20, textAlign: 'left', cursor: 'pointer', background: '#fff' }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
                <span style={{ width: 34, height: 34, display: 'grid', placeItems: 'center', borderRadius: 8, background: '#EEF2FF', color: '#4F46E5' }}>
                  <ShieldCheck size={18} />
                </span>
                {permissionSet.is_system && <span className="hv-badge hv-badge-gray">Default</span>}
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--hv-text-primary)' }}>{permissionSet.name}</div>
              <div style={{ minHeight: 38, marginTop: 6, fontSize: 12.5, lineHeight: 1.5, color: 'var(--hv-text-muted)' }}>
                {permissionSet.description || 'No description'}
              </div>
              <div style={{ marginTop: 14, fontSize: 12, fontWeight: 700, color: 'var(--hv-text-secondary)' }}>
                {(permissionSet.permissions || []).length} of {ALL_PERMISSIONS.length} permissions
              </div>
            </button>
          ))}
        </div>
      )}

      {editing && (
        <div className="permission-modal-overlay">
          <div className="permission-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800 }}>{editing === 'new' ? 'New Permission Set' : 'Edit Permission Set'}</div>
                <div style={{ marginTop: 4, fontSize: 13, color: 'var(--hv-text-muted)' }}>Changes apply to every user assigned to this set.</div>
              </div>
              <button className="hv-btn hv-btn-ghost" onClick={closeEditor}><X size={17} /></button>
            </div>

            <div style={{ display: 'grid', gap: 14 }}>
              <div>
                <label className="hv-form-label">Name</label>
                <input className="hv-input" value={form.name} onChange={event => setForm({ ...form, name: event.target.value })} disabled={selectedSet?.name === 'Organisation Admin'} />
              </div>
              <div>
                <label className="hv-form-label">Description</label>
                <input className="hv-input" value={form.description} onChange={event => setForm({ ...form, description: event.target.value })} />
              </div>

              {PERMISSION_GROUPS.map(group => (
                <div key={group.label} style={{ borderTop: '1px solid var(--hv-border)', paddingTop: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 800, marginBottom: 8 }}>{group.label}</div>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {group.permissions.map(permission => {
                      const locked = selectedSet?.name === 'Organisation Admin'
                      return (
                        <label key={permission.key} style={{ display: 'grid', gridTemplateColumns: '18px 1fr', gap: 10, padding: '9px 10px', border: '1px solid var(--hv-border-light)', borderRadius: 8, cursor: locked ? 'default' : 'pointer' }}>
                          <input type="checkbox" checked={locked || form.permissions.includes(permission.key)} onChange={() => togglePermission(permission.key)} disabled={locked} />
                          <span>
                            <span style={{ display: 'block', fontSize: 12.5, fontWeight: 700 }}>{permission.label}</span>
                            <span style={{ display: 'block', marginTop: 2, fontSize: 11.5, color: 'var(--hv-text-muted)' }}>{permission.description}</span>
                          </span>
                        </label>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginTop: 22 }}>
              <div>
                {selectedSet && !selectedSet.is_system && (
                  <button className="hv-btn hv-btn-danger" onClick={() => setDeleteTarget(selectedSet)}><Trash2 size={15} /> Delete</button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <button className="hv-btn hv-btn-secondary" onClick={closeEditor}>Cancel</button>
                <button className="hv-btn hv-btn-primary" onClick={saveSet} disabled={saving || selectedSet?.name === 'Organisation Admin'}>{saving ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <ConfirmModal
          title="Delete permission set"
          message={`Delete “${deleteTarget.name}”? Users assigned to it will need another permission set.`}
          confirmLabel="Delete Permission Set"
          onConfirm={deleteSet}
          onCancel={() => setDeleteTarget(null)}
          danger
        />
      )}
    </div>
  )
}
